import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as regular from "../services/api";
import * as superAdmin from "../services/superAdminApi";
import { PageHeader } from "../components/UI";
import invoiceLogo from "../assets/logo1.png";

const superServices = { ...superAdmin, getOrders: superAdmin.superAdminGetOrders };
const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const displayInvoiceLabel = (value) => String(value || "—")
  .toLowerCase()
  .replace(/\b\w/g, (character) => character.toUpperCase());
const getProtectedApiError = (error, fallback) => {
  const status = error.response?.status;
  if (status === 401) return "Your admin login token is missing or expired. Sign in again and retry.";
  if (status === 403) return "Only an active administrator or super-admin can use this API.";
  if (status === 404) return error.response?.data?.message || "Not found. Confirm the backend deployment includes the /api/v1/admin route and that the ID exists in this database.";
  return error.response?.data?.message || fallback;
};
const initialHsnForm = { code: "", description: "", gstRate: "", isActive: true };
const textileHsnReferences = [
  ["5007", "Woven silk fabrics"],
  ...["5111", "5112", "5113"].map((code) => [code, "Woven wool or fine animal hair fabrics"]),
  ...["5208", "5209", "5210", "5211", "5212"].map((code) => [code, "Woven cotton fabrics — verify fibre composition and tariff subheading"]),
  ["5407", "Woven synthetic filament yarn fabrics"],
  ...["5512", "5513", "5514", "5515", "5516"].map((code) => [code, "Woven artificial staple fibre fabrics"]),
  ...["6001", "6002", "6003", "6004", "6005", "6006"].map((code) => [code, "Knitted or crocheted fabrics"]),
  ...["6103", "6104", "6105", "6106", "6109"].map((code) => [code, "Knitted or crocheted garments — verify exact garment construction/classification"]),
  ...["6203", "6204", "6205", "6206", "6207", "6208", "6214"].map((code) => [code, "Woven garments — verify exact garment construction/classification"]),
  ...["6301", "6302", "6303", "6305"].map((code) => [code, "Made-up textile articles — verify exact tariff subheading"]),
];

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const escapeCsv = (value) => {
  const stringValue = value == null ? "" : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const matchesDateFilter = (orderDate, startDate, endDate) => {
  if (!orderDate) return false;
  const date = new Date(orderDate);
  if (Number.isNaN(date.getTime())) return false;

  const normalized = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);

  if (startDate && normalized < startDate) return false;
  if (endDate && normalized > endDate) return false;
  return true;
};

const buildBillingRows = (orders, filterType, selectedMonth, startDate, endDate) => {
  const monthKey = selectedMonth || "";

  const filtered = orders.filter((order) => {
    if (!order?.createdAt) return false;
    const created = toDateInputValue(order.createdAt);
    if (filterType === "single-day") return created === startDate;
    if (filterType === "date-range") return matchesDateFilter(order.createdAt, startDate, endDate);
    if (filterType === "monthly") {
      if (!monthKey) return false;
      const month = created.slice(0, 7);
      return month === monthKey;
    }
    return true;
  });

  const rows = [];
  filtered.forEach((order) => {
    const orderDate = toDateInputValue(order.createdAt || order.invoice?.invoiceDate || order.updatedAt);
    const customerName = order.address?.fullName || order.invoice?.customer?.name || order.userId || "—";
    const customerPhone = order.address?.phone || order.invoice?.customer?.phone || "—";
    const orderTotal = Number(order.totalAmount || order.invoice?.grandTotal || 0);

    if (!Array.isArray(order.items) || order.items.length === 0) {
      rows.push([
        orderDate,
        order._id || "",
        customerName,
        customerPhone,
        "—",
        "—",
        "",
        "",
        "0",
        "0",
        order.status || "Pending",
        String(orderTotal),
        order.paymentMethod || order.invoice?.paymentMethod || "",
      ]);
      return;
    }

    order.items.forEach((item) => {
      const itemName = item.name || item.productName || "—";
      const itemSku = item.sku || "—";
      const qty = Number(item.quantity || 0);
      const unitPrice = Number(item.finalPrice || item.price || item.variant?.price || 0);
      const itemTotal = qty * unitPrice;

      rows.push([
        orderDate,
        order._id || "",
        customerName,
        customerPhone,
        itemName,
        itemSku,
        String(item.productId || ""),
        qty,
        unitPrice,
        itemTotal,
        order.status || "Pending",
        String(orderTotal),
        order.paymentMethod || order.invoice?.paymentMethod || "",
      ]);
    });
  });

  return rows;
};

const downloadBillingCsv = ({ orders, reportType, selectedMonth, fromDate, toDate }) => {
  const rows = buildBillingRows(orders, reportType, selectedMonth, fromDate, toDate);

  if (rows.length === 0) {
    const emptyCsv = [
      ["Report Type", reportType],
      ["Selected Month", selectedMonth || ""],
      ["From Date", fromDate || ""],
      ["To Date", toDate || ""],
      ["Total Billing Amount", "0"],
      ["Total Orders", "0"],
      ["Order Date", "Order ID", "Customer Name", "Customer Phone", "Product Name", "SKU", "Product ID", "Quantity", "Unit Price", "Item Total", "Status", "Order Total", "Payment Method"],
    ].map((row) => row.map((cell) => escapeCsv(cell)).join(",")).join("\n");

    const blob = new Blob([emptyCsv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `billing-report-empty-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return;
  }

  const totalBillingAmount = rows.reduce((sum, row) => sum + Number(row[9] || 0), 0);
  const totalOrderCount = new Set(rows.map((row) => row[1])).size;
  const productCount = new Set(rows.map((row) => row[4])).size;

  const csvRows = [
    ["Report Type", reportType],
    ["Selected Month", selectedMonth || ""],
    ["From Date", fromDate || ""],
    ["To Date", toDate || ""],
    ["Total Billing Amount", totalBillingAmount],
    ["Total Orders", totalOrderCount],
    ["Products Included", productCount],
    [],
    ["Order Date", "Order ID", "Customer Name", "Customer Phone", "Product Name", "SKU", "Product ID", "Quantity", "Unit Price", "Item Total", "Status", "Order Total", "Payment Method"],
    ...rows,
  ];

  const csv = csvRows
    .map((row) => row.map((cell) => escapeCsv(cell)).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `billing-report-${reportType}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function BillingTax({ superAdmin: isSuperAdmin = false }) {
  const api = isSuperAdmin ? superServices : regular;
  const [tab, setTab] = useState("invoices");
  const [orders, setOrders] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [invoiceResponse, setInvoiceResponse] = useState(null);
  const [hsnForm, setHsnForm] = useState(initialHsnForm);
  const [editingHsnId, setEditingHsnId] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingHsn, setSavingHsn] = useState(false);
  const [uploadingHsn, setUploadingHsn] = useState(false);
  const [hsnExcelFile, setHsnExcelFile] = useState(null);
  const [deletingHsnId, setDeletingHsnId] = useState("");
  const hsnFileInput = useRef(null);
  const [query, setQuery] = useState("");
  const [reportType, setReportType] = useState("date-range");
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [fromDate, setFromDate] = useState(() => toDateInputValue(new Date()));
  const [toDate, setToDate] = useState(() => toDateInputValue(new Date()));

  const handleBillingDownload = () => {
    if (reportType === "monthly") {
      downloadBillingCsv({ orders, reportType, selectedMonth, fromDate, toDate });
      return;
    }

    if (reportType === "single-day") {
      if (!fromDate) return toast.error("Please choose a day to download.");
      downloadBillingCsv({ orders, reportType, selectedMonth, fromDate, toDate: fromDate });
      return;
    }

    if (!fromDate || !toDate) {
      toast.error("Please choose both start and end dates for the billing range.");
      return;
    }

    if (fromDate > toDate) {
      toast.error("Start date cannot be later than end date.");
      return;
    }

    downloadBillingCsv({ orders, reportType, selectedMonth, fromDate, toDate });
  };

  const loadHsn = useCallback(async () => {
    try {
      const firstPage = await api.getHsnMaster({ page: 1, limit: 100 });
      const pages = Number(firstPage.data?.pagination?.pages) || 1;
      const remainingPages = [];
      for (let page = 2; page <= pages; page += 10) {
        const pageBatch = await Promise.all(
          Array.from({ length: Math.min(10, pages - page + 1) }, (_, index) =>
            api.getHsnMaster({ page: page + index, limit: 100 })),
        );
        remainingPages.push(...pageBatch);
      }
      setHsnCodes([
        ...(firstPage.data?.hsnCodes || []),
        ...remainingPages.flatMap((response) => response.data?.hsnCodes || []),
      ]);
    } catch (error) {
      toast.error(getProtectedApiError(error, "Could not load HSN master."));
    }
  }, [api]);

  const loadOrders = useCallback(async () => {
    try {
      const response = await api.getOrders();
      setOrders(response.data?.orders || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not load orders for billing.");
    }
  }, [api]);

  useEffect(() => { loadHsn(); loadOrders(); }, [loadHsn, loadOrders]);

  const loadInvoice = async (orderId = selectedOrderId) => {
    if (!orderId) return;
    setLoading(true);
    setInvoiceResponse(null);
    try {
      const response = await api.getOrderInvoice(orderId);
      setInvoiceResponse(response.data);
    } catch (error) {
      toast.error(getProtectedApiError(error, "Unable to prepare this invoice."));
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async () => {
    if (!selectedOrderId) return;
    try {
      const response = await api.downloadInvoicePdf(selectedOrderId);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice?.invoiceNumber || "tax-invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not download invoice PDF.");
    }
  };

  const saveHsn = async (event) => {
    event.preventDefault();
    setSavingHsn(true);
    try {
      if (editingHsnId) await api.updateHsn(editingHsnId, hsnForm);
      else await api.createHsn(hsnForm);
      toast.success(editingHsnId ? "HSN master updated." : "HSN code added.");
      setHsnForm(initialHsnForm);
      setEditingHsnId("");
      await loadHsn();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not save HSN code.");
    } finally {
      setSavingHsn(false);
    }
  };

  const uploadHsnExcel = async () => {
    if (!hsnExcelFile) {
      toast.error("Choose an XLS or XLSX file to import.");
      return;
    }
    setUploadingHsn(true);
    try {
      const response = await api.uploadHsnExcel(hsnExcelFile);
      const summary = response.data?.summary || {};
      toast.success(`Imported ${summary.total || 0} HSN rows: ${summary.created || 0} added, ${summary.updated || 0} updated.`);
      setHsnExcelFile(null);
      if (hsnFileInput.current) hsnFileInput.current.value = "";
      await loadHsn();
    } catch (error) {
      const details = error.response?.data?.errors;
      const message = error.response?.data?.message || "Could not import HSN Excel file.";
      toast.error(Array.isArray(details) && details.length ? `${message} ${details.slice(0, 3).join(" ")}` : message);
    } finally {
      setUploadingHsn(false);
    }
  };

  const editHsn = (hsn) => {
    setEditingHsnId(hsn._id);
    setHsnForm({ code: hsn.code, description: hsn.description, gstRate: String(hsn.gstRate), isActive: hsn.isActive });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleHsn = async (hsn) => {
    try {
      await api.updateHsn(hsn._id, { isActive: !hsn.isActive });
      toast.success(`HSN ${hsn.code} ${hsn.isActive ? "deactivated" : "activated"}.`);
      loadHsn();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not update HSN status.");
    }
  };

  const deleteHsn = async (hsn) => {
    if (!window.confirm(`Delete HSN ${hsn.code}? Products keep their saved HSN details, but this code will be removed from the master.`)) return;
    setDeletingHsnId(hsn._id);
    try {
      await api.deleteHsn(hsn._id);
      if (editingHsnId === hsn._id) {
        setEditingHsnId("");
        setHsnForm(initialHsnForm);
      }
      toast.success(`HSN ${hsn.code} deleted.`);
      await loadHsn();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not delete HSN code.");
    } finally {
      setDeletingHsnId("");
    }
  };

  const visibleHsn = useMemo(() => {
    const value = query.trim().toLowerCase();
    return hsnCodes.filter((hsn) => !value || `${hsn.code} ${hsn.description}`.toLowerCase().includes(value));
  }, [hsnCodes, query]);

  const invoice = invoiceResponse?.invoice;
  const invoiceOrder = invoiceResponse?.order;

  return (
    <div className="space-y-5">
      <PageHeader title="Billing / GST Invoices" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[ ["invoices", "Invoices"], ["hsn", "HSN Master" ] ].map(([value, label]) => <button key={value} type="button" onClick={() => setTab(value)} className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === value ? "bg-[#640101] text-white" : "bg-white text-gray-600 shadow-sm"}`}>{label}</button>)}
        </div>
        {tab === "invoices" && (
          <button type="button" onClick={handleBillingDownload} className="btn-primary">
            Download Billing Report
          </button>
        )}
      </div>

      {tab === "invoices" && (
        <section className="kanchira-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#640101]">Billing Report Export</h2>
              <p className="text-sm text-gray-500">Download billing details by month, date range, or a single day.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <label className="text-sm font-medium text-gray-700">
              Report type
              <select className="kanchira-input mt-1" value={reportType} onChange={(event) => setReportType(event.target.value)}>
                <option value="monthly">Monthly total with product details</option>
                <option value="date-range">Date range billing</option>
                <option value="single-day">Single day billing</option>
              </select>
            </label>

            {reportType === "monthly" ? (
              <label className="text-sm font-medium text-gray-700">
                Month
                <input type="month" className="kanchira-input mt-1" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} />
              </label>
            ) : (
              <label className="text-sm font-medium text-gray-700">
                From date
                <input type="date" className="kanchira-input mt-1" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
              </label>
            )}

            {reportType !== "monthly" && (
              <label className="text-sm font-medium text-gray-700">
                To date
                <input type="date" className="kanchira-input mt-1" value={reportType === "single-day" ? fromDate : toDate} onChange={(event) => setToDate(event.target.value)} disabled={reportType === "single-day"} />
              </label>
            )}

            {reportType === "single-day" && (
              <div className="flex items-end">
                <span className="text-xs text-gray-500">
                  This export includes all orders placed on the selected date.
                </span>
              </div>
            )}
          </div>
        </section>
      )}

      {tab === "hsn" ? <>
        <section className="kanchira-card space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-[#640101]">Import HSN Master from Excel</h2>
            <p className="text-sm text-gray-500">Upload an XLS or XLSX file with HSN Code, Description and GST Rate columns. Existing HSN codes are updated; new codes are added. Maximum file size: 5 MB.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="min-w-0 flex-1 text-sm font-medium text-gray-700">Excel file
              <input ref={hsnFileInput} type="file" accept=".xlsx,.xls" className="kanchira-input mt-1" onChange={(event) => setHsnExcelFile(event.target.files?.[0] || null)} />
            </label>
            <button type="button" className="btn-primary" disabled={!hsnExcelFile || uploadingHsn} onClick={uploadHsnExcel}>{uploadingHsn ? "Importing..." : "Upload Excel"}</button>
          </div>
        </section>
        <section className="kanchira-card">
          <h2 className="mb-4 text-lg font-semibold text-[#640101]">{editingHsnId ? "Edit HSN master" : "Add HSN code"}</h2>
          {!editingHsnId && <label className="mb-4 block text-sm font-medium text-gray-700">Common textile reference (prefills code and description only; verify the tariff classification and current GST rate)<select className="kanchira-input mt-1" defaultValue="" onChange={(event) => { const [code, description] = textileHsnReferences[Number(event.target.value)] || []; if (code) setHsnForm((current) => ({ ...current, code, description, gstRate: "" })); }}><option value="">Choose a reference or enter a verified code manually</option>{textileHsnReferences.map(([code, description], index) => <option key={code} value={index}>{code} · {description}</option>)}</select></label>}
          <form onSubmit={saveHsn} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">HSN code<input required minLength={4} maxLength={8} pattern="[0-9]{4,8}" inputMode="numeric" className="kanchira-input mt-1" placeholder="e.g. 711319" value={hsnForm.code} onChange={(event) => setHsnForm({ ...hsnForm, code: event.target.value.replace(/\D/g, "").slice(0, 8) })} /></label>
            <label className="text-sm font-medium text-gray-700">GST rate (%)<input required type="number" min="0" max="100" step="0.01" className="kanchira-input mt-1" placeholder="e.g. 3" value={hsnForm.gstRate} onChange={(event) => setHsnForm({ ...hsnForm, gstRate: event.target.value })} /></label>
            <label className="text-sm font-medium text-gray-700 md:col-span-2">HSN description<input required className="kanchira-input mt-1" placeholder="Description of goods" value={hsnForm.description} onChange={(event) => setHsnForm({ ...hsnForm, description: event.target.value })} /></label>
            <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={hsnForm.isActive} onChange={(event) => setHsnForm({ ...hsnForm, isActive: event.target.checked })} />Active for product assignment</label>
            <div className="flex flex-wrap justify-end gap-2 md:col-span-2">
              {editingHsnId && <button type="button" className="btn-secondary" onClick={() => { setEditingHsnId(""); setHsnForm(initialHsnForm); }}>Cancel Edit</button>}
              <button type="submit" className="btn-primary" disabled={savingHsn}>{savingHsn ? "Saving..." : editingHsnId ? "Update HSN" : "Add HSN"}</button>
            </div>
          </form>
        </section>
        <section className="kanchira-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-[#640101]">HSN Master Table</h2>
            <input className="kanchira-input w-full sm:max-w-xs" placeholder="Search HSN / description" value={query} onChange={(event) => setQuery(event.target.value)} />
          </div>
          <div className="overflow-x-auto"><table className="w-full min-w-[700px] text-left text-sm"><thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>{["HSN Code", "Description", "GST Rate", "Status", "Last Updated", "Actions"].map((label) => <th key={label} className="px-3 py-3">{label}</th>)}</tr></thead><tbody>{visibleHsn.map((hsn) => <tr key={hsn._id} className="border-t"><td className="px-3 py-3 font-mono font-semibold">{hsn.code}</td><td className="px-3 py-3">{hsn.description}</td><td className="px-3 py-3">{hsn.gstRate}%</td><td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${hsn.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{hsn.isActive ? "Active" : "Inactive"}</span></td><td className="px-3 py-3">{hsn.updatedAt ? new Date(hsn.updatedAt).toLocaleString("en-IN") : "—"}</td><td className="px-3 py-3"><div className="flex gap-2"><button type="button" className="btn-secondary text-xs" onClick={() => editHsn(hsn)}>Edit</button><button type="button" className="btn-secondary text-xs" onClick={() => toggleHsn(hsn)}>{hsn.isActive ? "Deactivate" : "Activate"}</button><button type="button" className="btn-secondary text-xs text-red-700" disabled={deletingHsnId === hsn._id} onClick={() => deleteHsn(hsn)}>{deletingHsnId === hsn._id ? "Deleting..." : "Delete"}</button></div></td></tr>)}{visibleHsn.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-gray-500">No HSN codes found.</td></tr>}</tbody></table></div>
        </section>
      </> : <>
        <section className="kanchira-card space-y-3">
          <h2 className="text-lg font-semibold text-[#640101]">Invoice by Order</h2>
          <p className="text-sm text-gray-500">Select a verified order to prepare and view its invoice.</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select className="kanchira-input min-w-0 flex-1" value={selectedOrderId} onChange={(event) => { setSelectedOrderId(event.target.value); setInvoiceResponse(null); }}><option value="">Select order</option>{orders.map((order) => <option key={order._id} value={order._id}>{order.invoice?.invoiceNumber || order._id?.slice(-8).toUpperCase()} · {order.address?.fullName || order.userId} · {new Date(order.createdAt).toLocaleDateString("en-IN")} · {money(order.totalAmount)}</option>)}</select>
            <button className="btn-primary" type="button" disabled={!selectedOrderId || loading} onClick={() => loadInvoice()}>{loading ? "Loading..." : "View Invoice"}</button>
          </div>
        </section>
        {invoice && invoiceOrder && <section className="invoice-print-area mx-auto max-w-[794px] space-y-3 bg-white p-5 text-[11px] leading-[1.35] text-black shadow-sm">
          <header className="text-center">
            <img src={invoiceLogo} alt="Kanchira logo" className="mx-auto h-[70px] w-[70px] object-contain" />
            <h2 className="mt-1 text-xl font-bold">{invoice.seller?.registeredName || "Kanchira Enterprises"}</h2>
            <p className="mt-1">{invoice.seller?.address || "H No 16-2-227/249&250, SARDHAR PATEL NAGAR, NEAR JNTU, Kukatpally, Hyderabad, Medchal Malkajgiri, Telangana - 500085"}</p>
            <p>Phone: {invoice.seller?.phone || "9963353171"} &nbsp; Email: {invoice.seller?.email || "support@kanchira.com"} &nbsp; Website: {invoice.seller?.website || "kanchira.com"}</p>
            <p>GSTIN: {invoice.seller?.gstin || "36FACPK8396A1ZK"}</p>
            <h3 className="mt-4 text-base font-bold">
              {invoice.taxDataComplete === false ? "INVOICE PREVIEW - TAX DETAILS INCOMPLETE" : "TAX INVOICE - ORIGINAL FOR RECIPIENT"}
            </h3>
            {invoice.taxDataComplete === false && (
              <div className="mx-auto mt-2 max-w-2xl rounded border border-amber-300 bg-amber-50 p-2 text-left text-xs text-amber-900">
                <p className="font-semibold">Tax classification is incomplete. This is a preview, not a final tax invoice.</p>
                <ul className="mt-1 list-inside list-disc">
                  {(invoice.taxWarnings || []).map((warning, index) => <li key={`${index}-${warning}`}>{warning}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-2 flex justify-center gap-2 print-hide">
              <button className="btn-primary" type="button" onClick={downloadInvoice}>Download PDF</button>
              <button className="btn-secondary" type="button" onClick={() => window.print()}>Print</button>
            </div>
          </header>

          <table className="w-full border-collapse border border-gray-400 text-[10px]">
            <tbody>
              <tr className="border-b border-gray-300">
                <td className="w-1/2 border-r border-gray-300 p-1.5"><b>Invoice Number:</b> {invoice.invoiceNumber}</td>
                <td className="p-1.5"><b>Sale Type:</b> {displayInvoiceLabel(invoice.saleType)}</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="break-all border-r border-gray-300 p-1.5"><b>PhonePe Transaction ID:</b> {invoice.phonepeTransactionId || invoiceOrder.phonepeTransactionId || "—"}</td>
                <td className="p-1.5"><b>Date:</b> {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "—"}</td>
              </tr>
              <tr>
                <td className="border-r border-gray-300 p-1.5"><b>Time:</b> {invoice.invoiceTime || "—"}</td>
                <td className="p-1.5"><b>Payment Method:</b> {displayInvoiceLabel(invoice.paymentMethod || invoiceOrder.paymentMethod)}</td>
              </tr>
            </tbody>
          </table>

          <section className="border border-gray-400 p-2">
            <h3 className="mb-1 font-bold">Customer Details</h3>
            <div className="grid grid-cols-3 gap-x-2 gap-y-1">
              <p><b>Name:</b> {invoice.customer?.name || "—"}</p>
              <p><b>Phone:</b> {invoice.customer?.phone || "—"}</p>
              <p className="break-all"><b>Email:</b> {invoice.customer?.email || "—"}</p>
              <p><b>City:</b> {invoice.customer?.city || "—"}</p>
              <p><b>PIN Code:</b> {invoice.customer?.pincode || "—"}</p>
              <p className="col-span-3"><b>Address:</b> {invoice.customer?.address || "—"}</p>
              <p className="col-span-3">
                <b>Billing State:</b> {invoice.billingState || "—"} ({invoice.billingStateCode || "—"}) |
                <b> Customer State:</b> {invoice.customerState || "—"} ({invoice.customerStateCode || "—"}) |
                <b> Place of Supply:</b> {invoice.placeOfSupply || "—"} |
                <b> GST Treatment:</b> {invoice.gstTreatment || invoice.supplyType || "—"}
              </p>
              <p className="col-span-3"><b>Reverse Charge:</b> {invoice.reverseCharge ? "Yes" : "No"}</p>
            </div>
          </section>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse border border-gray-400 text-[10px]">
              <thead className="bg-gray-200">
                <tr>{["Product ID", "Product Name", "HSN", "Qty", "Price", "Subtotal", "Discount", "Item Total"].map((heading) => <th key={heading} className="border border-gray-400 px-1.5 py-2 font-medium">{heading}</th>)}</tr>
              </thead>
              <tbody>{(invoice.items || []).map((item, index) => (
                <tr key={`${item.productId}-${index}`}>
                  <td className="border border-gray-400 p-1.5">{item.productCode || item.sku || item.productId || "—"}</td>
                  <td className="border border-gray-400 p-1.5">{item.productName || "—"}</td>
                  <td className="border border-gray-400 p-1.5">{item.hsnCode || "—"}</td>
                  <td className="border border-gray-400 p-1.5 text-right">{item.quantity}</td>
                  <td className="whitespace-nowrap border border-gray-400 p-1.5 text-right">{money(item.unitPrice ?? item.unitPriceInclusive)}</td>
                  <td className="whitespace-nowrap border border-gray-400 p-1.5 text-right">{money(item.itemSubtotal ?? item.subtotal)}</td>
                  <td className="whitespace-nowrap border border-gray-400 p-1.5 text-right">{money(item.itemDiscount)}</td>
                  <td className="whitespace-nowrap border border-gray-400 p-1.5 text-right">{money(item.lineTotal)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>

          <div className="ml-auto max-w-[330px] space-y-1">
            <table className="w-full border-collapse border border-gray-400 text-[10px]">
              <tbody>
                {[
                  ["Taxable Amount", money(invoice.taxableValue)],
                  ["CGST", money(invoice.cgst)],
                  ["SGST", money(invoice.sgst)],
                  ["IGST", money(invoice.igst)],
                  ["Total GST", money(invoice.totalTax)],
                  ["Subtotal", money(invoice.subtotal)],
                  [`Discount (${invoice.subtotal ? (invoice.discount * 100 / invoice.subtotal).toFixed(2) : "0.00"}%)`, money(invoice.discount)],
                  ...(Number(invoice.deliveryCharge) > 0 ? [["Delivery", money(invoice.deliveryCharge)]] : []),
                  ["Grand Total", money(invoice.grandTotal)],
                ].map(([label, value]) => (
                  <tr key={label} className="border-b border-gray-200 last:border-0">
                    <td className="p-1 text-right font-semibold">{label}:</td>
                    <td className="w-[42%] p-1 text-right">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="w-full border-collapse border border-gray-400 text-[10px]">
              <thead><tr><th colSpan="2" className="border-b border-gray-300 p-1 text-left">Payment Details</th></tr></thead>
              <tbody>
                {[
                  ["Payment Type", displayInvoiceLabel(invoice.paymentMethod || invoiceOrder.paymentMethod)],
                  ["UPI Received", money(invoice.upiReceived)],
                  ["Total Received", money(invoice.totalReceived)],
                  ["Balance Due", money(invoice.balanceDue)],
                  ["Payment Status", displayInvoiceLabel(invoice.paymentStatus || invoiceOrder.paymentStatus)],
                ].map(([label, value]) => (
                  <tr key={label} className="border-b border-gray-200 last:border-0">
                    <td className="p-1">{label}:</td><td className="p-1 text-right">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className="space-y-1 pt-2 text-[10px]">
            <p><b>Exchange Policy:</b> {invoice.exchangePolicy || "Exchange is allowed only within 3 days of purchase."}</p>
            <p><b>Amount in Words:</b> {invoice.amountInWords || "—"}</p>
            <p>Prices are {invoice.priceIncludesGst === false ? "exclusive" : "inclusive"} of GST.</p>
            <p><b>Reverse Charge:</b> {invoice.reverseCharge ? "Yes" : "No"}</p>
            <p className="pt-2 text-center font-semibold">Thank you for shopping with {invoice.seller?.registeredName || "Kanchira Enterprises"}.</p>
          </footer>
        </section>}
      </>}
      <style>{`@media print { @page { size: A4 portrait; margin: 12mm; } body * { visibility: hidden !important; } .invoice-print-area, .invoice-print-area * { visibility: visible !important; } .invoice-print-area { position: absolute !important; inset: 0 !important; width: 100% !important; max-width: none !important; box-shadow: none !important; border: 0 !important; padding: 0 !important; font-size: 8pt !important; } .print-hide { display:none !important; } .invoice-print-area table { font-size: 7pt !important; } .invoice-print-area th, .invoice-print-area td { padding: 3px !important; } }`}</style>
    </div>
  );
}
